import styled from "styled-components/macro";
import { TrackerStepState } from "./trackerSteps";

export const TrackerContainer = styled.div`
  padding: 10px 20px 18px 20px;
  min-width: 0;
`;

export const TrackerHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  padding: 0 0 10px 0;
  margin: 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
`;

export const TrackerHeaderStatic = styled.div`
  padding: 0;
`;

export const TrackerTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.greyMedium};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0 0 16px 0;
`;

export const TrackerHeaderLabel = styled.span`
  color: ${({ theme }) => theme.greyLight};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.02em;
  white-space: nowrap;
`;

export const OrderStatusHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 92px auto;
  align-items: center;
  column-gap: 16px;
  padding: 0;
`;

export const DotRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

export const Dot = styled.span<{ filled: boolean }>`
  display: inline-flex;
  align-items: center;
  font-size: 8px;
  color: ${({ theme, filled }) =>
    filled ? theme.greenSuccess : theme.neutral300};
`;

/* Horizontal segmented "pizza tracker" bar (minimal hide-date variant). */
export const SegmentBar = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin-top: 14px;
`;

export const Segment = styled.span<{ filled: boolean }>`
  height: 4px;
  background-color: ${({ theme, filled }) =>
    filled ? theme.greenSuccess : theme.neutral200};
`;

export const TrackerChevron = styled.span`
  color: ${({ theme }) => theme.greyMedium};
  font-size: 14px;
  display: inline-flex;
`;

/* ----- Minimal style ----- */

export const MinimalRow = styled.div`
  display: grid;
  grid-template-columns: 92px 30px minmax(0, 1fr);
  align-items: center;
  column-gap: 16px;
  min-width: 0;
`;

/* Collapsed detailed view: mirror the expanded TimelineRow columns (40px icon)
 * so the date/icon/label line up exactly with the open timeline. */
export const DetailedCollapsedRow = styled.div`
  display: grid;
  grid-template-columns: 92px 40px minmax(0, 1fr);
  align-items: center;
  column-gap: 16px;
  min-width: 0;
`;

export type TrackerAccent = "purple" | "neutral";

export const TrackerDate = styled.span<{ accent?: TrackerAccent }>`
  color: ${({ theme, accent }) =>
    accent === "neutral" ? theme.greyMedium : theme.purpleMedium};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  white-space: nowrap;
`;

export const TrackerIcon = styled.span<{
  active: boolean;
  accent?: TrackerAccent;
  $leftAlign?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: ${({ $leftAlign }) =>
    $leftAlign ? "flex-start" : "center"};
  width: ${({ accent }) => (accent === "neutral" ? "30px" : "40px")};
  height: ${({ accent }) => (accent === "neutral" ? "32px" : "40px")};
  border-radius: 50%;
  font-size: ${({ accent }) => (accent === "neutral" ? "26px" : "20px")};
  color: ${({ theme, active, accent }) => {
    if (!active) {
      return theme.greyLight;
    }
    return accent === "neutral" ? theme.greyMedium : theme.purpleMediumDark;
  }};
  /* Detailed timeline: completed/current stages sit in a light-purple circle. */
  background-color: ${({ theme, active, accent }) =>
    active && accent !== "neutral" ? theme.purpleLight : "transparent"};
`;

export const TrackerLabel = styled.span<{
  active: boolean;
  accent?: TrackerAccent;
}>`
  color: ${({ theme, active, accent }) => {
    if (!active) {
      return theme.greyMedium;
    }
    return accent === "neutral" ? theme.greyMedium : theme.purpleMediumDark;
  }};
  font-weight: 700;
  font-size: 16px;
  line-height: 1.5;
`;

export const TrackerSubtext = styled.span`
  display: block;
  color: ${({ theme }) => theme.greyLight};
  font-size: 14px;
  line-height: 20px;
  margin-top: 3px;
`;

/* ----- Detailed style ----- */

export const TimelineList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const TimelineRow = styled.li`
  display: grid;
  grid-template-columns: 92px 40px minmax(0, 1fr);
  align-items: flex-start;
  column-gap: 16px;
  min-height: 62px;
`;

export const TimelineDate = styled.span<{ visible: boolean }>`
  color: ${({ theme }) => theme.purpleMediumDark};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  white-space: nowrap;
  padding-top: 10px;
  visibility: ${({ visible }) => (visible ? "visible" : "hidden")};
`;

export const TimelineIconColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
`;

export const TimelineConnector = styled.div<{ state: TrackerStepState }>`
  flex: 1;
  width: 3px;
  min-height: 16px;
  background-color: ${({ theme, state }) =>
    state === "upcoming" ? theme.neutral300 : theme.purpleMedium};
`;

export const TimelineLabelWrapper = styled.div`
  padding-top: 8px;
  min-width: 0;
`;
